/**
 * Two-tier tool system:
 * - Tier 1: always-loaded tools with full schemas (the `core` manifests), fixed per
 *   page whatever is on screen (preloadForPanels only measures a panel preload)
 * - Tier 2: on-demand tools, listed as text in the system prompt (grouped by domain
 *   heading, then panel) and loaded with get_tools when needed
 *
 * Scope registry:
 * - Tools are organised by scope (page context). Each scope is a ToolScopeDefinition
 *   that declares which tools are available and an optional system-prompt extension.
 * - A GLOBAL pseudo-scope contributes tools available on every page (including
 *   unknown/unregistered pages).
 * - The three built-in scopes (dashboard, history, backtest) are registered at module
 *   init so consumers see zero behavior change after the registry refactor.
 */

import type { ToolDefinition } from '../types/api'
import type { TurnAccess } from '../types/companion'
import {
  declarationCovers,
  type CapabilityDeclaration,
  type Operation,
  type SharedCapability,
  type ToolEffect,
  type ToolManifest,
  type ToolTags,
} from './tool-manifest'
import { DOMAINS } from './domains'
import {
  GET_TOOLS_TOOL,
  GET_PLATFORM_GUIDE_TOOL,
  REQUEST_PANEL_SELECTION_TOOL,
  SEARCH_KNOWLEDGE_TOOL,
  ASK_ANALYST_TOOL,
  SUMMARIZE_HISTORY_TOOL,
  SET_LANGUAGE_TOOL,
  READ_PANEL_STATE_TOOL,
  VIEW_SCREEN_TOOL,
  TOOL_MAP,
  TOOL_TAGS,
} from './tool-schemas'

export type { ToolEffect } from './tool-manifest'

// ── Manifests ─────────────────────────────────────────────────────────────────
//
// One manifest per tool (spec 2026-10-18 AI companion growth architecture, 4.1),
// kept by name beside the definitions: `ToolDefinition` stays the shape sent to
// the model, so nothing new rides on every turn. The static tools' manifests
// are in tool-schemas.ts; the app passes its panel tools' manifests to
// `registerToolDefinition`.
//
// A manifest's `effect` says what a call does to the trader's workspace
// (2026-10-15, one-assistant Phase 2; the classes are in tool-manifest.ts). The
// read-only tool gate (below), the frontend approve/undo flow and the confirm
// card all read it: a minimal version of the build-operations spec's "every
// effect returns its inverse"
// (docs/superpowers/specs/2026-09-30-companion-build-operations-design.md).

const _manifests = new Map<string, ToolManifest>()

/**
 * Tools that only READ or move the view: the `read` and `view` effects. Kept as
 * a Set, in step with the manifests, for the read-only gate below and the
 * frontend approve/undo flow.
 */
export const READ_ONLY_TOOLS: ReadonlySet<string> = new Set()

function setManifest(name: string, tags: ToolTags): void {
  _manifests.set(name, { name, surface: 'browser', ...tags })
  const readOnly = READ_ONLY_TOOLS as Set<string>
  if (tags.effect === 'read' || tags.effect === 'view') readOnly.add(name)
  else readOnly.delete(name)
}

for (const [name, tags] of Object.entries(TOOL_TAGS)) setManifest(name, tags)

/** A browser tool's manifest, when it has one. */
export function getToolManifest(name: string): ToolManifest | undefined {
  return _manifests.get(name)
}

/**
 * Whether a tool asks even when the trader turned on "apply without asking":
 * the changes that remove something (`alwaysAsk` on its manifest).
 */
export function toolAlwaysAsks(name: string): boolean {
  return _manifests.get(name)?.alwaysAsk === true
}

/** Every browser tool manifest registered so far (the static ones, then the app's). */
export function listToolManifests(): ToolManifest[] {
  return [..._manifests.values()]
}

// ── Shared capabilities (spec 4.4) ────────────────────────────────────────────
//
// Filters and columns span many panels. Panel types and pages declare the
// shared capabilities they implement, with the operations each supports; the
// app declares them together with the adapters that implement them
// (apps/frontend/src/features/companion/panel-capabilities.ts). A capability
// tool reaches every surface that declares it, and nothing lists panel types.

const _panelCapabilities = new Map<string, CapabilityDeclaration>()
const _pageCapabilities = new Map<string, CapabilityDeclaration>()

/** A panel type implements a shared capability, with these operations. */
export function declarePanelCapability(
  panelType: string,
  capability: SharedCapability,
  operations: readonly Operation[],
): void {
  _panelCapabilities.set(panelType, {
    ..._panelCapabilities.get(panelType),
    [capability]: operations,
  })
}

/** A page implements a shared capability itself (History's page-wide filter). */
export function declarePageCapability(
  page: string,
  capability: SharedCapability,
  operations: readonly Operation[],
): void {
  _pageCapabilities.set(page, { ..._pageCapabilities.get(page), [capability]: operations })
}

/** Every declaration, for the catalog and the tests. */
export function capabilityDeclarations(): {
  panels: Record<string, CapabilityDeclaration>
  pages: Record<string, CapabilityDeclaration>
} {
  return {
    panels: Object.fromEntries(_panelCapabilities),
    pages: Object.fromEntries(_pageCapabilities),
  }
}

/** graph_call methods that save, switch or run rather than edit: they ask first. */
const GRAPH_CALL_CONFIRM_METHODS: ReadonlySet<string> = new Set([
  'renameStrategy',
  'saveStrategy',
  'newStrategy',
  'loadStrategy',
  'duplicateStrategy',
  'setAutoRun',
  'run',
])

/** Whether a manifest declares this tool's effect (tests assert every offered tool's does). */
export function hasExplicitEffect(name: string): boolean {
  return _manifests.has(name)
}

/**
 * The effect of one call. `graph_call` asks first for its lifecycle and run
 * methods; its edits take the manifest's effect. A tool with no manifest asks
 * first: the safe default for anything new.
 */
export function toolEffect(call: { name: string; input?: Record<string, unknown> }): ToolEffect {
  const method = call.input?.method
  if (
    call.name === 'graph_call' &&
    typeof method === 'string' &&
    GRAPH_CALL_CONFIRM_METHODS.has(method)
  )
    return 'confirm'
  return _manifests.get(call.name)?.effect ?? 'confirm'
}

/**
 * Terminal-answer tools: their output IS the finished, user-facing answer
 * (already-synthesized cited prose), not raw data. When every tool in a turn is
 * terminal and succeeded, the model's follow-up call would only *rephrase* an
 * already-final answer — wasted latency + tokens, and a chance to mangle
 * citations. So we emit their output directly and skip that call. A subset of
 * READ_ONLY_TOOLS.
 */
export const TERMINAL_TOOLS: ReadonlySet<string> = new Set([
  'ask_analyst',
  'summarize_history',
  // search_knowledge is NOT terminal: it returns raw recipe hits (titles,
  // scores, config JSON) that the model must turn into an answer. Treating it
  // as terminal showed users "No results found" and raw hit lists verbatim
  // (prod, 2026-09-28..30).
  // get_platform_guide is NOT unconditionally terminal: its PROSE result is
  // documentation the model must rephrase into a short reply + a POINT tag. But
  // when it launches a guided walkthrough it marks that result `passthrough`
  // (the authored intro IS the answer) — see terminalPassthrough below.
])

/**
 * Passthrough decision for the tool loop. Returns the concatenated finished
 * answer when every tool call in a turn is a terminal-answer tool AND each
 * result either succeeded OR is a `passthrough` failure (a deterministic,
 * already-user-friendly error like "out of credits" / "temporarily
 * unavailable") — so the loop can short-circuit the redundant follow-up
 * ("rephrase") LLM call and the user sees the exact intended message. Returns
 * null when the loop should continue normally: no tools, any non-terminal tool
 * (raw data → needs synthesis; a mutation → needs confirmation/chaining), or a
 * NON-passthrough failure (an errored answer is better phrased by the model
 * than shown raw).
 *
 * A result is terminal when EITHER its tool is a TERMINAL_TOOL and it succeeded,
 * OR the result is explicitly flagged `passthrough` — a tool that is normally
 * non-terminal (e.g. get_platform_guide) but produced a verbatim finished answer
 * this time (a launched walkthrough's authored intro, or a friendly failure).
 */
export function terminalPassthrough(
  toolCalls: ReadonlyArray<{ name: string }>,
  toolResults: ReadonlyArray<{ content: string; success: boolean; passthrough?: boolean }>,
): string | null {
  if (toolCalls.length === 0 || toolResults.length !== toolCalls.length) return null
  const allTerminal = toolCalls.every((tc, i) => {
    const r = toolResults[i]
    return r.passthrough || (TERMINAL_TOOLS.has(tc.name) && r.success)
  })
  return allTerminal ? toolResults.map((tr) => tr.content).join('\n\n') : null
}

// ── PageContext ───────────────────────────────────────────────────────────────

/** Route-derived page / scope string. Widened to string so custom scopes can
 *  be registered without touching this type. The three built-in values are
 *  kept as documentation, not as an exhaustive union. */
export type PageContext = string

// ── Tool scope definition ─────────────────────────────────────────────────────

/**
 * Descriptor for the tools available in a given scope (page context).
 *
 * `tools` lists the on-demand (Tier 2) tools for this scope. Which panels a
 * tool needs on screen comes from its manifest: a tool about panel types needs
 * one of them, and a capability tool needs the page or a panel on screen to
 * declare its capability. Every other tool is included when the scope is
 * active.
 *
 * `promptExtras` is appended to the system prompt when this scope's tools are
 * active (e.g. the backtest action principle lives here).
 */
export interface ToolScopeDefinition {
  scope: string
  tools: Array<{
    name: string
    desc: string
  }>
  /** Extra text appended to the system prompt when this scope is active. */
  promptExtras?: string
}

// ── Registry store ────────────────────────────────────────────────────────────

/** Internal key for the global pseudo-scope. */
const GLOBAL_SCOPE_KEY = '__GLOBAL__'

const _scopeRegistry = new Map<string, ToolScopeDefinition>()

/**
 * Register a scope definition. Re-registering the same `scope` string
 * replaces the previous definition (last-write wins).
 */
export function registerToolScope(def: ToolScopeDefinition): void {
  _scopeRegistry.set(def.scope, def)
}

// ── Runtime tool definitions ──────────────────────────────────────────────────
//
// `TOOL_MAP` is a static table and ships in every bundle. Feature code that must
// stay out of user builds (EdgeScript, dev-gated) registers its tool schemas here
// at runtime instead, from behind its own flag; `get_tools` resolves through
// `getToolDefinition` so both tables look the same to the model.

const _runtimeDefinitions = new Map<string, ToolDefinition>()

/**
 * Register (or replace) a tool schema at runtime, with its manifest. Static
 * tools cannot be overridden. A tool registered without a manifest has no
 * declared effect, so it asks first.
 */
export function registerToolDefinition(def: ToolDefinition, tags?: ToolTags): void {
  _runtimeDefinitions.set(def.name, def)
  if (tags) setManifest(def.name, tags)
}

/** Every definition: the static table, then the ones registered at runtime. */
export function listToolDefinitions(): ToolDefinition[] {
  return [
    ...Object.values(TOOL_MAP),
    ...[..._runtimeDefinitions.values()].filter((d) => !(d.name in TOOL_MAP)),
  ]
}

/** The full definition for a tool name: the static table first, then runtime ones. */
export function getToolDefinition(name: string): ToolDefinition | undefined {
  return TOOL_MAP[name] ?? _runtimeDefinitions.get(name)
}

/**
 * Retrieve the registered definition for a scope, or `undefined` if the
 * scope has not been registered (unknown routes, for example).
 */
export function getRegisteredScope(scope: string): ToolScopeDefinition | undefined {
  return _scopeRegistry.get(scope)
}

// ── Always-loaded tools ───────────────────────────────────────────────────────

/** Tools always sent with full schemas (read-only, meta) */
const ALWAYS_LOADED: ToolDefinition[] = [
  GET_TOOLS_TOOL,
  // App how-to retrieval — always directly callable (not demand-loaded via
  // get_tools), so the model can answer "how do I…" without a discovery round.
  GET_PLATFORM_GUIDE_TOOL,
  REQUEST_PANEL_SELECTION_TOOL,
  SEARCH_KNOWLEDGE_TOOL,
  ASK_ANALYST_TOOL,
  SET_LANGUAGE_TOOL,
  READ_PANEL_STATE_TOOL,
  // Screenshot on demand: the model decides it needs to see the screen.
  VIEW_SCREEN_TOOL,
]

/**
 * Returns the always-loaded tool definitions (with full schemas).
 * These are the only tools sent in the API `tools` parameter, the same whatever
 * panels are on screen (2026-10-19: a prompt-cache design that keeps the list
 * fixed per surface is being weighed, so no panel preload is sent).
 * All other tools are listed in the system prompt as text and loaded via get_tools.
 */
export function getToolsForContext(_panelTypes: string[], page: PageContext): ToolDefinition[] {
  const tools = [...ALWAYS_LOADED]
  // summarize_history needs the History page's active filters, so it's only
  // offered there (directly callable, no get_tools round).
  if (page === 'history') tools.push(SUMMARIZE_HISTORY_TOOL)
  return tools
}

// ── What a panel preload would add (phase 4, measured and not sent) ──────────

/**
 * The tools a panel preload would send with full schemas on this screen: every
 * tool the page offers (scopes and access gate, as the index) whose manifest
 * names a panel type on screen. Spec 2026-10-18 section 4.2 preloads them; on
 * 2026-10-19 that was held back while a prompt-cache design that keeps the tool
 * list fixed per surface is weighed. So nothing sends these: they stay in the
 * index, and this only measures what the preload would add (companion-api's
 * mirror of it is checked against it, browser-scopes.unit.test.ts).
 */
export function preloadForPanels(
  panelTypes: string[],
  page: PageContext,
  access?: TurnAccess,
): ToolDefinition[] {
  return getIndexedTools(panelTypes, page, access).flatMap((t) => {
    const def = aboutPanelOnScreen(t.name, panelTypes) ? getToolDefinition(t.name) : undefined
    return def ? [def] : []
  })
}

/** Whether a tool's manifest names a panel type on screen. */
function aboutPanelOnScreen(name: string, panelTypes: string[]): boolean {
  return (_manifests.get(name)?.panels ?? []).some((type) => panelTypes.includes(type))
}

// ── Public query API ──────────────────────────────────────────────────────────

/**
 * Returns a text index of available tools for the system prompt.
 * Claude reads this to know what tools exist, then calls get_tools to load schemas.
 *
 * On a read-only turn the index is restricted to read-only tools, so Clicky is never
 * even told that mutation tools exist.
 */
export function getToolIndex(
  panelTypes: string[],
  page: PageContext,
  access?: TurnAccess,
  filter?: (name: string) => boolean,
): string {
  return formatToolIndex(getIndexedTools(panelTypes, page, access, filter))
}

const INDEX_TITLE = '## additional tools (call get_tools to load schema before using)'

/** A domain's place in the registry; a tool with no registered domain goes last. */
const DOMAIN_RANK = new Map(DOMAINS.map((d, i) => [d.id, i]))

/**
 * The index text, one line per tool, grouped by domain heading, then panel
 * (spec 2026-10-18 AI companion growth architecture, sections 0 and 7): a tool
 * sits under its first domain, in registry order, and the tools about a panel
 * type or a shared capability form a group of their own after the domain's
 * others. Every line after the title starts with "- ", the shape
 * CompanionManager strips a stale index by when the page changes.
 */
function formatToolIndex(tools: ReadonlyArray<{ name: string; desc: string }>): string {
  if (tools.length === 0) return ''
  const groups = new Map<string, { rank: number; on: string; label: string; lines: string[] }>()
  for (const t of tools) {
    const m = _manifests.get(t.name)
    const rank = DOMAIN_RANK.get(m?.domains[0] ?? '') ?? DOMAINS.length
    const on = m?.capability ?? (m?.panels.length ? `${m.panels.join(', ')} panel` : '')
    const heading = DOMAINS[rank]?.heading ?? 'Other tools'
    const key = `${rank}:${on}`
    const group = groups.get(key) ?? {
      rank,
      on,
      label: on ? `${heading} (${on})` : heading,
      lines: [],
    }
    group.lines.push(`- ${t.name}: ${t.desc}`)
    groups.set(key, group)
  }
  // Stable: a domain's panel groups keep the order they were first offered in.
  const ordered = [...groups.values()].sort(
    (a, b) => a.rank - b.rank || Number(a.on !== '') - Number(b.on !== ''),
  )
  return [INDEX_TITLE, ...ordered.flatMap((g) => [`- ${g.label}:`, ...g.lines])].join('\n')
}

/**
 * Names of the on-demand tools available for this context — used to gate
 * tool-specific prompt context (e.g. the node catalog) on what the model can
 * actually load via get_tools, not just the always-loaded schemas.
 */
export function getIndexedToolNames(
  panelTypes: string[],
  page: PageContext,
  access?: TurnAccess,
  filter?: (name: string) => boolean,
): string[] {
  return getIndexedTools(panelTypes, page, access, filter).map((t) => t.name)
}

// ── Internal tool resolution ──────────────────────────────────────────────────

/**
 * Whether a scope entry is offered on this page with these panel types on
 * screen. A capability tool needs the page, or a panel on screen, to declare
 * its capability with every operation it performs; a tool about panel types
 * (its manifest's `panels`) needs one of them.
 */
function offeredWith(t: { name: string }, panelTypes: string[], page: PageContext): boolean {
  const m = _manifests.get(t.name)
  if (m?.capability)
    return (
      declarationCovers(_pageCapabilities.get(page), m) ||
      panelTypes.some((type) => declarationCovers(_panelCapabilities.get(type), m))
    )
  const panels = m?.panels ?? []
  return panels.length === 0 || panelTypes.some((type) => panels.includes(type))
}

/**
 * Resolves the active tool list for a given page + panel configuration.
 *
 * Order: global scope tools first, then page-scoped tools (filtered by `when`).
 * Unknown pages receive only the global tools. On a read-only turn, mutation
 * tools are dropped, and only READ_ONLY_TOOLS survive.
 */
function getIndexedTools(
  panelTypes: string[],
  page: PageContext,
  access?: TurnAccess,
  filter?: (name: string) => boolean,
): Array<{ name: string; desc: string }> {
  const tools: Array<{ name: string; desc: string }> = []

  // Global scope — always included regardless of page
  const globalScope = _scopeRegistry.get(GLOBAL_SCOPE_KEY)
  if (globalScope) {
    for (const t of globalScope.tools) {
      if (offeredWith(t, panelTypes, page)) tools.push({ name: t.name, desc: t.desc })
    }
  }

  // Page-specific scope (if registered)
  const scopeDef = _scopeRegistry.get(page)
  if (scopeDef) {
    for (const t of scopeDef.tools) {
      if (offeredWith(t, panelTypes, page)) tools.push({ name: t.name, desc: t.desc })
    }
  }

  // Read-only turn: strip anything that could mutate the workspace.
  const gated = access === 'read' ? tools.filter((t) => READ_ONLY_TOOLS.has(t.name)) : tools
  // Host filter (persona surface restriction) composes with the access gate.
  return filter ? gated.filter((t) => filter(t.name)) : gated
}

// ── Backtest action principle (moved here from CompanionManager) ───────────────

/**
 * Backtest pages bias the model toward DOING instead of explaining — the
 * default failure mode is narrating manual steps the tools could perform.
 *
 * Lives here as the source of truth so it can be embedded in the backtest
 * scope's `promptExtras` and re-exported for legacy consumers.
 */
export const BACKTEST_ACTION_PRINCIPLE = `## backtest editor
graph_read/graph_call: public editor API; graph_read help lists signatures. supports create/load/save, grouping, layout, observations, auto-run, single awaited runs. no strategy deletion, batch runs, arbitrary execution, or live trading. authorized research-and-backtest continues after analyst grounding without per-edit permission. max 3 dispatched runs/turn; timeout means unknown: never auto-retry. analyze_backtest requires executionId from the current owned run; missing artifacts are unavailable. preserve completeness labels; tested examples are not population claims.
build/modify/fix: DO IT with tools. BUILD: get_tools loads create_strategy_from_recipe and get_node_specs for parameters; execute. read_current_graph returns a recipe.
EDIT: prefer transactional apply_graph_ops for all changes; any failure leaves everything unchanged. legacy add_node/connect_nodes/set_node_parameters: single trivial mutations only.
ALWAYS read_current_graph before proposing or applying any edit; never edit from memory. read_backtest_results before making any performance claim or improvement proposal.
"improve"/"make it better": read_backtest_results AND read_current_graph, present 2-3 concrete changes with one-line rationale each; apply in ONE apply_graph_ops batch.
after improvement, offer to re-run; if already requested, call rerun_backtest immediately.
manual how-to only when explicitly requested.`

// ── Default scope registrations ───────────────────────────────────────────────

// Global pseudo-scope — tools available on every page, including unknown routes.
registerToolScope({
  scope: GLOBAL_SCOPE_KEY,
  tools: [
    // get_platform_guide is in ALWAYS_LOADED (directly callable), not here.
    { name: 'navigate_to', desc: 'Navigate to a different page in the app' },
    { name: 'read_saved_strategy', desc: 'Read an owned saved strategy graph by ID' },
    { name: 'create_saved_strategy', desc: 'Create a saved node-graph strategy from a recipe' },
    { name: 'edit_saved_strategy', desc: 'Apply validated graph operations to a saved strategy' },
    { name: 'run_saved_strategy', desc: 'Run one ticker and date on a saved strategy' },
    { name: 'get_node_specs', desc: 'Read parameter and connection details for node types' },
    { name: 'analyze_backtest', desc: 'Analyze artifacts from the current owned execution' },
  ],
})

// dashboard scope
registerToolScope({
  scope: 'dashboard',
  tools: [
    // Panel management
    { name: 'add_panel', desc: 'Add a panel (chart, scanner, watchlist, news, etc.)' },
    { name: 'delete_panel', desc: 'Remove a panel' },
    { name: 'set_ticker', desc: 'Change active ticker on a panel' },
    { name: 'set_focus_date', desc: 'Set historical date or reset to live' },
    { name: 'set_link_mode', desc: 'Set panel ticker link mode' },
    // Filters and columns: offered with a panel on screen that declares them.
    {
      name: 'set_filters',
      desc: 'Set filters by column (replaces same-column, preserves others). Use for both adds and updates.',
    },
    { name: 'clear_filters', desc: 'Remove all filters from a panel' },
    { name: 'add_columns', desc: 'Append columns to a panel' },
    { name: 'remove_columns', desc: 'Remove columns from a panel' },
    { name: 'set_columns', desc: 'Replace all columns on a panel' },
    // News-specific: its manifest names the news panel.
    { name: 'set_news_sources', desc: 'Toggle news/SEC filing sources' },
  ],
})

// history scope
registerToolScope({
  scope: 'history',
  tools: [
    {
      name: 'generate_history_layout',
      desc: 'Generate analysis layout from description',
    },
    { name: 'add_panel', desc: 'Add a panel' },
    // The page declares its page-wide filter (the app's panel-capabilities.ts),
    // so set_filters is offered here with no panel on screen; clear_filters and
    // the column tools need a panel on screen that declares them.
    {
      name: 'set_filters',
      desc: 'Set filters by column (replaces same-column, preserves others). Use for both adds and updates.',
    },
    // The old name, kept working until the companion prompt and actions.md say set_filters.
    {
      name: 'set_history_filters',
      desc: 'Old name for set_filters on this page: sets the page-wide History filters',
    },
    { name: 'clear_filters', desc: 'Remove all filters from a panel' },
    { name: 'add_columns', desc: 'Append columns to a panel' },
    { name: 'remove_columns', desc: 'Remove columns from a panel' },
    { name: 'set_columns', desc: 'Replace all columns on a panel' },
  ],
})

// backtest scope — promptExtras carries the action principle
registerToolScope({
  scope: 'backtest',
  tools: [
    {
      name: 'graph_read',
      desc: 'Inspect the active graph, strategy, chart layout, and exact execution results; help lists method signatures',
    },
    {
      name: 'graph_call',
      desc: 'Edit, organize, save/load/create strategies, configure auto-run, and run one backtest (no strategy deletion or batch execution)',
    },
    { name: 'read_current_graph', desc: 'Read strategy graph (recipe form)' },
    { name: 'read_backtest_results', desc: 'Read latest backtest results' },
    { name: 'create_strategy_from_recipe', desc: 'Build strategy from recipe' },
    {
      name: 'apply_graph_ops',
      desc: 'Batch-edit the current graph transactionally (preferred for multi-step edits)',
    },
    {
      name: 'rerun_backtest',
      desc: 'Re-run the backtest on the current strategy (reuses the last run inputs)',
    },
    { name: 'create_strategy_template', desc: 'Load a pre-built strategy' },
    { name: 'add_node', desc: 'Add a node to strategy graph' },
    { name: 'connect_nodes', desc: 'Connect two nodes' },
    { name: 'set_node_parameters', desc: 'Update node parameters' },
    { name: 'remove_node', desc: 'Remove a node' },
  ],
  promptExtras: BACKTEST_ACTION_PRINCIPLE,
})
